import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import Modal from '../../../../components/Modal';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import CustomizedSwitch from '../../../../components/Switch';
import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import MasterLookupService from '../../../../services/master-lookup.service';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';


interface CategoryRow {
  id: number;
  category_code: string;
  category_name: string;
  display_order: number;
  status: string;
}

const CategoryList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'display_order' });
  const [items, setItems] = useState<CategoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CategoryRow | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const { loading } = useFetch({
    service: MasterLookupService,
    func: 'list',
    payload: { type: 'categories', includeDeleted: showInactive, page, limit: perPage },
    dependency: [showInactive, page, perPage, refreshToken],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.masters}${AuthRoutes.categories}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const response: any = await MasterLookupService.remove('categories', deleteTarget.id);
      const softOrHard = response?.data?.softOrHard;
      toast.success(
        <Toast
          message={
            softOrHard === 'soft'
              ? t('masters.categories.list.pageTexts.deleteSuccessSoft')
              : t('masters.categories.list.pageTexts.deleteSuccessHard')
          }
        />,
        { icon: false }
      );
      setRefreshToken((v) => v + 1);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, t]);

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'category_code', sortEnabled: true, width: '20%', label: t('masters.categories.list.pageTexts.table.columns.code') },
      { id: 'category_name', sortEnabled: true, width: '30%', label: t('masters.categories.list.pageTexts.table.columns.name') },
      { id: 'display_order', sortEnabled: true, width: '15%', label: t('masters.categories.list.pageTexts.table.columns.displayOrder') },
      { id: 'status', sortEnabled: true, width: '15%', label: t('masters.categories.list.pageTexts.table.columns.status') },
      { id: 'actions', sortEnabled: false, width: '20%', label: t('masters.categories.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: CategoryRow; className?: string }) => (
      <tr className={className}>
        <td>{item.category_code}</td>
        <td>{item.category_name}</td>
        <td>{item.display_order}</td>
        <td>
          <StatusBadge label={item.status} colour={item.status === 'active' ? '#2e7d32' : '#757575'} />
        </td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => handleEdit(item.id),
              },
              {
                id: 'delete',
                label: t('common.delete') || 'Delete',
                icon: <i className="ri-delete-bin-line" />,
                danger: true,
                onClick: () => setDeleteTarget(item),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [handleEdit]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('masters.categories.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('masters.categories.list.pageTexts.title')}</h4>
        <div className="d-flex align-items-center gap-4">
          <CustomizedSwitch
            label={t('masters.categories.list.pageTexts.showInactive')}
            name="showInactive"
            checked={showInactive}
            onChange={() => setShowInactive((v) => !v)}
          />
          <Button
            className="btn btn-primary"
            text={t('masters.categories.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.categories}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('masters.categories.list.pageTexts.deleteDialog.title')}
        content={t('masters.categories.list.pageTexts.deleteDialog.body')}
        action={
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('masters.categories.list.pageTexts.deleteDialog.cancel')}
              onClick={() => setDeleteTarget(null)}
            />
            <Button
              className="btn btn-danger"
              text={t('masters.categories.list.pageTexts.deleteDialog.confirm')}
              onClick={handleDeleteConfirm}
            />
          </>
        }
      />
    </>
  );
};

export default CategoryList;
